import React, { useState, useEffect, useCallback } from 'react';

// ==================================================================================
// IMPORTAÇÃO DA API CENTRALIZADA
// ==================================================================================
import api from '../../services/api';

/* =============================================================
   1. CONFIGURAÇÕES & DESIGN SYSTEM
   ============================================================= */
const THEME = {
  primary: '#4f46e5',   // Indigo
  bg: '#f8fafc',        // Slate Claro
  surface: '#ffffff',
  text: '#0f172a',      // Slate Escuro
  muted: '#64748b',     // Slate Médio
  border: '#e2e8f0',
  success: '#10b981',
  danger: '#ef4444',
  warning: '#f59e0b',
  
  // Cores Semânticas
  status: {
    pending: { bg: '#fff7ed', text: '#c2410c', border: '#ffedd5', icon: '#ea580c', label: 'Lente Pendente' }, 
    ordered: { bg: '#eff6ff', text: '#1d4ed8', border: '#dbeafe', icon: '#3b82f6', label: 'Pedido Realizado' }, 
    lab:     { bg: '#faf5ff', text: '#7e22ce', border: '#f3e8ff', icon: '#a855f7', label: 'No Laboratório' }, 
    ready:   { bg: '#f0fdf4', text: '#15803d', border: '#dcfce7', icon: '#22c55e', label: 'Pronto na Loja' }, 
    done:    { bg: '#f1f5f9', text: '#475569', border: '#e2e8f0', icon: '#94a3b8', label: 'Entregue' }  
  }
};

/* =======================
   UTILITÁRIOS
======================= */
const formatCurrency = (val) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val || 0);

const parseMoney = (valStr) => {
  if (!valStr) return 0;
  if (typeof valStr === 'number') return valStr;
  let v = valStr.toString().replace(/[^0-9,.]/g, '');
  v = v.replace(',', '.');
  const floatVal = parseFloat(v);
  return isNaN(floatVal) ? 0 : floatVal;
};

// FIX DE DATA (TIMEZONE): Trata data como string YYYY-MM-DD local
const formatDateLocal = (dateString) => {
  if (!dateString) return '--/--/----';
  try {
    const datePart = dateString.split('T')[0]; // Pega YYYY-MM-DD
    const [year, month, day] = datePart.split('-');
    return `${day}/${month}/${year}`;
  } catch (e) { return dateString; }
};

// Helper seguro para renderizar nome de cliente/paciente se for objeto
const safeRenderName = (objOrStr) => {
  if (!objOrStr) return '';
  if (typeof objOrStr === 'string') return objOrStr;
  if (typeof objOrStr === 'object' && objOrStr.nome) return objOrStr.nome;
  return 'Nome não informado';
};

// Ícones SVG (Ultra Clean)
const Icons = {
  ArrowLeft: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>,
  Eye: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>,
  Lab: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 2v7.31"></path><path d="M14 2v7.31"></path><path d="M8.5 2h7"></path><path d="M14 9.3a6.5 6.5 0 1 1-4 0"></path></svg>,
  Box: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path><polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline><line x1="12" y1="22.08" x2="12" y2="12"></line></svg>,
  Check: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"></polyline></svg>,
  User: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>,
  Search: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>,
  FilterX: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>,
  Calendar: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>,
  Whatsapp: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>,
  Alert: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>,
  Close: () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>,
  Image: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg>,
  Send: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>,
  Edit: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>,
  Trash: () => <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>,
  Plus: () => <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  FileText: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
};

/* =============================================================
   2. COMPONENTES UI
   ============================================================= */

// Linha de Informação
const InfoRow = ({ label, value }) => {
  let displayValue = value || '-';
  if (typeof value === 'object' && value !== null) {
     if (value.nome) displayValue = value.nome;
     else if (value.toString && value.toString() !== '[object Object]') displayValue = value.toString();
     else displayValue = '...';
  }
  return (
    <div style={{marginBottom: 10, flex: 1, minWidth: '140px'}}>
      <span style={{display:'block', fontSize: 10, fontWeight:'700', color: THEME.muted, textTransform:'uppercase', marginBottom: 3, letterSpacing: '0.5px'}}>{label}</span>
      <span style={{fontSize: 14, color: THEME.text, fontWeight: 600, wordBreak: 'break-word', display: 'block'}}>{displayValue}</span>
    </div>
  );
};

// Card de Status KPI
const StatCard = ({ label, count, themeKey, active, onClick, icon }) => {
  const s = THEME.status[themeKey] || THEME.status.done;
  return (
    <div onClick={onClick} className={`stat-card-premium ${active ? 'active' : ''}`} style={{ borderColor: active ? s.text : 'transparent', boxShadow: active ? `0 10px 25px -5px ${s.text}40` : '0 4px 6px -1px rgba(0,0,0,0.02)' }}>
      <div className="stat-card-header">
        <span className="stat-icon-bg" style={{ background: active ? s.text : s.bg, color: active ? '#fff' : s.text }}>{icon}</span>
        <span className="stat-count" style={{color: active ? s.text : THEME.text}}>{count}</span>
      </div>
      <span className="stat-label">{label}</span>
      {active && <div className="active-indicator" style={{background: s.text}}></div>}
    </div>
  );
};

// Badge de Status
const Badge = ({ status }) => {
  let s = THEME.status.done;
  if (status === 'Pedido da lente') s = THEME.status.pending;
  if (status === 'Pedido realizado') s = THEME.status.ordered;
  if (status === 'No laboratório') s = THEME.status.lab;
  if (status === 'Pronto na loja') s = THEME.status.ready;
  if (status === 'Entregue') s = THEME.status.done;

  return (
    <span style={{ background: s.bg, color: s.text, border: `1px solid ${s.border}`, padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: '700', textTransform: 'uppercase', display: 'inline-flex', alignItems: 'center', gap: 6, letterSpacing: '0.03em' }}>
      <span style={{width:6, height:6, borderRadius:'50%', background:s.icon}}></span>
      {status}
    </span>
  );
};

// Toast Notification
const Toast = ({ msg, type }) => {
  if (!msg) return null;
  const bg = type === 'error' ? '#ef4444' : '#10b981'; 
  return (
    <div className="toast-animation" style={{ position: 'fixed', bottom: 40, right: 40, background: bg, color: '#fff', padding: '14px 24px', borderRadius: 12, boxShadow: '0 10px 30px -5px rgba(0,0,0,0.2)', zIndex: 2000, display: 'flex', alignItems: 'center', gap: 12, fontWeight: '600', fontSize: 14, backdropFilter: 'blur(10px)' }}>
      {type === 'error' ? <Icons.Alert /> : <Icons.Check />} {msg}
    </div>
  );
};

/* =============================================================
   3. DASHBOARD PRINCIPAL (OSList)
   ============================================================= */
export default function OSList({ onBack }) {
  const [list, setList] = useState([]);
  const[loading, setLoading] = useState(true);
  const [selectedOS, setSelectedOS] = useState(null);

  const today = new Date();
  const currentMonthName = today.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
  const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];

  const [filters, setFilters] = useState({
    search: '',
    loja: '',
    status: '',
    dataInicio: startOfMonth,
    dataFim: endOfMonth
  });

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/ordens-servico');
      setList(Array.isArray(response.data) ? response.data : []);
    } catch (err) { 
      console.error(err); 
    } finally { 
      setLoading(false); 
    }
  },[]);

  useEffect(() => { fetchList(); }, [fetchList]);

  // Filtragem Lógica
  const statsBase = list.filter(item => {
    if (filters.dataInicio || filters.dataFim) {
      const itemDate = new Date(item.dataCompra).setHours(0,0,0,0);
      const start = filters.dataInicio ? new Date(filters.dataInicio).setHours(0,0,0,0) : null;
      const end = filters.dataFim ? new Date(filters.dataFim).setHours(0,0,0,0) : null;
      if (start && itemDate < start) return false;
      if (end && itemDate > end) return false;
    }
    if (filters.loja && item.loja !== filters.loja) return false;
    return true;
  });

  const counts = {
    total: statsBase.length,
    lens: statsBase.filter(o => o.status === 'Pedido da lente').length,
    ordered: statsBase.filter(o => o.status === 'Pedido realizado').length,
    lab: statsBase.filter(o => o.status === 'No laboratório').length,
    ready: statsBase.filter(o => o.status === 'Pronto na loja').length,
    delivered: statsBase.filter(o => o.status === 'Entregue').length,
  };

  const filteredList = statsBase.filter(item => {
    if (filters.status && item.status !== filters.status) return false;
    const term = filters.search.toLowerCase();
    
    const clientName = safeRenderName(item.cliente).toLowerCase();
    const osNum = item.numeroOS ? item.numeroOS.toString() : '';
    const cvNum = item.produto?.numeroCV ? item.produto.numeroCV.toLowerCase() : '';

    return clientName.includes(term) || osNum.includes(term) || cvNum.includes(term);
  });

  const handleClearFilters = () => {
    setFilters({ search: '', loja: '', status: '', dataInicio: '', dataFim: '' });
  };

  return (
    <div className="dashboard-container">
      <GlobalStyles />

      {/* HEADER */}
      <header className="dash-header">
        <div className="header-left">
           <button onClick={onBack} className="btn-back"><Icons.ArrowLeft /> Voltar</button>
           <div className="header-titles">
             <h1 className="page-title">Controle de Produção</h1>
             <p className="page-subtitle">
                Visão de <strong style={{color: THEME.primary}}>{(filters.dataInicio === startOfMonth && filters.dataFim === endOfMonth) ? currentMonthName : 'Período Personalizado'}</strong>
             </p>
           </div>
        </div>
        <div className="total-badge">
           <span className="badge-label">Total Período</span>
           <span className="badge-value">{counts.total}</span>
        </div>
      </header>

      {/* KPI CARDS */}
      <div className="stats-grid">
         <StatCard label="Pedir Lente" count={counts.lens} themeKey="pending" icon={<Icons.Alert />} active={filters.status === 'Pedido da lente'} onClick={() => setFilters({...filters, status: filters.status === 'Pedido da lente' ? '' : 'Pedido da lente'})} />
         <StatCard label="Pedido Feito" count={counts.ordered} themeKey="ordered" icon={<Icons.Box />} active={filters.status === 'Pedido realizado'} onClick={() => setFilters({...filters, status: filters.status === 'Pedido realizado' ? '' : 'Pedido realizado'})} />
         <StatCard label="No Laboratório" count={counts.lab} themeKey="lab" icon={<Icons.Lab />} active={filters.status === 'No laboratório'} onClick={() => setFilters({...filters, status: filters.status === 'No laboratório' ? '' : 'No laboratório'})} />
         <StatCard label="Pronto na Loja" count={counts.ready} themeKey="ready" icon={<Icons.Check />} active={filters.status === 'Pronto na loja'} onClick={() => setFilters({...filters, status: filters.status === 'Pronto na loja' ? '' : 'Pronto na loja'})} />
         <StatCard label="Entregues" count={counts.delivered} themeKey="done" icon={<Icons.User />} active={filters.status === 'Entregue'} onClick={() => setFilters({...filters, status: filters.status === 'Entregue' ? '' : 'Entregue'})} />
      </div>

      {/* TOOLBAR */}
      <div className="toolbar-container">
        <div className="search-wrapper">
           <Icons.Search />
           <input className="search-input" placeholder="Buscar Cliente, OS ou Código CV..." value={filters.search} onChange={e => setFilters({...filters, search: e.target.value})} />
        </div>

        <div className="filters-wrapper">
          <div className="date-group">
             <span className="input-label">Início</span>
             <input type="date" className="date-input" value={filters.dataInicio} onChange={e => setFilters({...filters, dataInicio: e.target.value})} />
             <span className="date-separator">Até</span>
             <input type="date" className="date-input" value={filters.dataFim} onChange={e => setFilters({...filters, dataFim: e.target.value})} />
          </div>
          
          <select className="select-input" value={filters.loja} onChange={e => setFilters({...filters, loja: e.target.value})}>
             <option value="">Todas as Lojas</option>
             <option value="Vizum 01">Vizum 01</option>
             <option value="Vizum 02">Vizum 02</option>
          </select>

          <button onClick={() => setFilters(prev => ({ ...prev, dataInicio: startOfMonth, dataFim: endOfMonth }))} className="btn-month" title="Voltar para Mês Atual">
             <Icons.Calendar />
          </button>
          
          {(filters.status || filters.search || filters.loja) && (
            <button onClick={handleClearFilters} className="btn-clear" title="Limpar filtros">
                <Icons.FilterX />
            </button>
          )}
        </div>
      </div>

      {/* LISTA */}
      {loading ? (
        <div className="loading-state"><div className="spinner"></div><p>Carregando produção...</p></div>
      ) : (
        <div className="grid-cards">
           {filteredList.length === 0 ? (
             <div className="empty-state">
                <div className="empty-icon"><Icons.Search /></div>
                <p>Nenhuma ordem encontrada para os filtros.</p>
                <button onClick={handleClearFilters} className="btn-link">Limpar filtros</button>
             </div>
           ) : (
             filteredList.map(os => <OSCard key={os._id} os={os} onClick={() => setSelectedOS(os._id)} />)
           )}
        </div>
      )}

      {selectedOS && <OSDetailModal osId={selectedOS} onClose={() => setSelectedOS(null)} onUpdate={fetchList} />}
    </div>
  );
}

/* =============================================================
   4. COMPONENTE DE CARD (CARD DA LISTA)
   ============================================================= */
const OSCard = ({ os, onClick }) => {
  const isPendingLens = os.status === 'Pedido da lente';
  const isMsgPending = os.statusmsg === 'Pendente';
  
  // Cálculo de Progresso de Pagamento
  const total = os.valorTotal || 0;
  const pago = os.pagamentos?.reduce((acc, p) => acc + (p.valor || 0), 0) || 0;
  const percentPago = total > 0 ? Math.min((pago / total) * 100, 100) : 0;

  return (
    <div className="os-card" onClick={onClick}>
      <div className="os-card-header">
        <div className="card-badges">
            <span className="cv-badge">CV {os.produto?.numeroCV || '---'}</span>
            <span className="os-number-badge">OS #{os.numeroOS || '---'}</span>
        </div>
        <span className="store-text">{os.loja}</span>
      </div>
      
      <div className="os-card-body">
        <h3 className="client-name">{safeRenderName(os.cliente)}</h3>
        
        {os.produto?.lente ? (
           <div className="lens-info"><Icons.Eye /> <span>{os.produto.lente}</span></div>
        ) : (
           <p className="no-lens">Lente não informada</p>
        )}

        <div className="progress-bar-wrapper">
            <div className="progress-labels">
                <span>Pago: {formatCurrency(pago)}</span>
                <span>{Math.round(percentPago)}%</span>
            </div>
            <div className="progress-track">
                <div className="progress-fill" style={{width: `${percentPago}%`, background: percentPago >= 100 ? THEME.success : THEME.primary}}></div>
            </div>
        </div>
      </div>

      <div className="alert-stack">
        {isPendingLens && <div className="alert-box alert-orange"><Icons.Alert /> PEDIR LENTE</div>}
        {isMsgPending && <div className="alert-box alert-green"><Icons.Whatsapp /> MSG PENDENTE</div>}
      </div>

      <div className="os-card-footer">
         <div className="date-info">
            <span className="date-label">ENTRADA</span>
            <span className="date-value">{formatDateLocal(os.dataCompra)}</span>
         </div>
         <Badge status={os.status} />
      </div>
    </div>
  );
};

/* =============================================================
   5. MODAL DE DETALHES & EDIÇÃO COMPLETA
   ============================================================= */
function OSDetailModal({ osId, onClose, onUpdate }) {
  const [os, setOs] = useState(null);
  const [activeTab, setActiveTab] = useState('geral');
  const [fotoExpandida, setFotoExpandida] = useState(null);
  const [toast, setToast] = useState(null);
  const [status, setStatus] = useState('');
  const [obs, setObs] = useState('');
  const [saving, setSaving] = useState(false);
  const [previewMsg, setPreviewMsg] = useState('');
  
  // FORMULÁRIO DE EDIÇÃO COMPLETO
  const [editForm, setEditForm] = useState({ 
    loja: '', vendedor: '',
    cliente: '', cpf: '', dataNascimento: '', paciente: '', whatsapp: '',
    cep: '', endereco: '', numero: '', complemento: '', bairro: '', cidade: '', uf: '',
    dataCompra: '', dataEntrega: '', 
    lente: '', armacao: '', cv: '',
    valorTotal: '', pagamentos: [],
    nfceUrl: '', chaveNfe: ''
  });
  
  const [novoPagamento, setNovoPagamento] = useState({ metodo: 'Dinheiro', valor: '', parcelas: 1 });
  const metodosPagamento = ['Dinheiro', 'PIX', 'Débito', 'Crédito'];

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  const fetchData = useCallback(async () => {
    try {
      const response = await api.get(`/ordens-servico/${osId}`);
      const data = response.data;
      setOs(data); 
      setStatus(data.status);

      // Preenche o formulário de edição com SAFETY CHECKS e DATA CORRETA
      setEditForm({
        loja: data.loja || '',
        vendedor: data.vendedor || '',
        
        cliente: typeof data.cliente === 'object' ? (data.cliente?.nome || '') : (data.cliente || ''),
        cpf: data.cliente?.cpf || '',
        dataNascimento: data.cliente?.dataNascimento ? data.cliente.dataNascimento.split('T')[0] : '',
        
        paciente: typeof data.paciente === 'object' ? (data.paciente?.nome || '') : (data.paciente || ''),
        whatsapp: data.whatsapp || '',

        cep: data.endereco?.cep || '',
        endereco: data.endereco?.logradouro || '',
        numero: data.endereco?.numero || '',
        complemento: data.endereco?.complemento || '',
        bairro: data.endereco?.bairro || '',
        cidade: data.endereco?.cidade || '',
        uf: data.endereco?.uf || '',

        // Formata data para o input type="date"
        dataCompra: data.dataCompra ? data.dataCompra.split('T')[0] : '',
        dataEntrega: data.dataEntrega ? data.dataEntrega.split('T')[0] : '',
        
        lente: data.produto?.lente || '', 
        armacao: data.produto?.armacao || '',
        cv: data.produto?.numeroCV || '',
        
        valorTotal: data.valorTotal || 0, 
        pagamentos: data.pagamentos || [],
        
        nfceUrl: data.nfceUrl || '',
        chaveNfe: data.chaveNfe || ''
      });
    } catch (err) { alert('Erro ao carregar detalhes'); }
  },[osId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const addPayment = () => {
      const val = parseMoney(novoPagamento.valor);
      if (val <= 0) return alert("Digite um valor válido.");
      setEditForm(prev => ({ ...prev, pagamentos:[...prev.pagamentos, { id: Date.now(), metodo: novoPagamento.metodo, valor: val, parcelas: novoPagamento.metodo === 'Crédito' ? novoPagamento.parcelas : 1 }] }));
      setNovoPagamento({ metodo: 'Dinheiro', valor: '', parcelas: 1 });
  };

  const removePayment = (id) => {
      setEditForm(prev => ({ ...prev, pagamentos: prev.pagamentos.filter(p => p.id !== id && p._id !== id) }));
  };

  const valorTotalNum = parseMoney(editForm.valorTotal);
  const totalPago = editForm.pagamentos.reduce((acc, p) => acc + (p.valor || 0), 0);
  const restante = valorTotalNum - totalPago;

  useEffect(() => {
    if (!os) return;
    const nome = safeRenderName(os.cliente).toLowerCase()?.split(' ')?.slice(0, 2)?.map(p => p.charAt(0).toUpperCase() + p.slice(1))?.join(' ') || 'Cliente';
    const nomeLoja = os.loja || 'Ótica Vizum';
    const dadosLojas = { 
      'Vizum 01': { endereco: 'Rua Coronel Pinto, 508C – Centro\nBoa Vista/RR', mapa: 'https://maps.app.goo.gl/Cr2jG47kn7oo3hx67', telefone: '(95) 99115-2904' }, 
      'Vizum 02': { endereco: 'Av. Sebastião Diniz, nº 806 – Sala 29\nCentro', mapa: 'https://maps.app.goo.gl/dvcR9ammUNHvzKPj6', telefone: '(95) 9 9118-8598' } 
    };
    const lojaAtual = dadosLojas[nomeLoja] || dadosLojas['Vizum 01'];

    if (activeTab === 'automacao') {
      if (status === 'Pronto na loja') {
        setPreviewMsg(`Olá, ${nome}! 😊\n\n✨ *SEUS ÓCULOS CHEGARAM!* ✨\n\nTemos uma ótima notícia: seus óculos já estão prontos e aguardando a sua retirada. 🎉👓\n\n📍 *${nomeLoja}*\n${lojaAtual.endereco}\n\n📌 *Localização:* ${lojaAtual.mapa}\n📲 *Contato:* ${lojaAtual.telefone}\n\nEstamos à sua disposição! 😄`);
      } else {
        setPreviewMsg('');
      }
    }
  },[os, activeTab, status]);

  const handleSaveStatus = async () => {
    setSaving(true);
    try {
      await api.put(`/ordens-servico/${os._id}`, { status, registroTexto: obs });
      showToast('Status atualizado!'); onUpdate(); fetchData(); setObs('');
    } catch (e) { showToast('Erro ao salvar status', 'error'); } finally { setSaving(false); }
  };

  const handleEditSave = async () => {
    setSaving(true);
    try {
      const payload = { 
        loja: editForm.loja,
        vendedor: editForm.vendedor,
        whatsapp: editForm.whatsapp,
        dataCompra: editForm.dataCompra,
        dataEntrega: editForm.dataEntrega || null,
        valorTotal: parseFloat(valorTotalNum),
        pagamentos: editForm.pagamentos,
        nfceUrl: editForm.nfceUrl,
        chaveNfe: editForm.chaveNfe,
        
        cliente: { 
            nome: editForm.cliente,
            cpf: editForm.cpf,
            dataNascimento: editForm.dataNascimento
        },
        
        paciente: {
            nome: editForm.paciente
        },
        
        endereco: {
            cep: editForm.cep,
            logradouro: editForm.endereco,
            numero: editForm.numero,
            complemento: editForm.complemento,
            bairro: editForm.bairro,
            cidade: editForm.cidade,
            uf: editForm.uf
        },

        produto: { 
            lente: editForm.lente, 
            armacao: editForm.armacao, 
            numeroCV: editForm.cv 
        },
        
        registroTexto: "📝 Dados da OS editados manualmente." 
      };

      await api.put(`/ordens-servico/${os._id}`, payload);
      showToast('Dados editados com sucesso!'); onUpdate(); fetchData(); setActiveTab('geral');
    } catch (e) { showToast('Erro ao editar dados', 'error'); } finally { setSaving(false); }
  };

  const handleDispararBoasVindas = async () => {
    if (!os.whatsapp) return showToast('Sem número cadastrado', 'error');
    if (!window.confirm("Deseja disparar a mensagem de Boas-Vindas com foto para o cliente?")) return;
    
    setSaving(true);
    try {
      await api.post(`/ordens-servico/${os._id}/boas-vindas`);
      showToast('Mensagem de Boas-Vindas enviada!', 'success');
      onUpdate();
      fetchData();
    } catch (error) {
      showToast('Erro ao disparar mensagem.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSendWhatsapp = async () => {
    if (!os.whatsapp) return showToast('Sem número cadastrado', 'error');
    if (!previewMsg) return showToast('A mensagem está vazia', 'error');
    
    setSaving(true);
    const phone = '55' + os.whatsapp.replace(/\D/g, ''); 
    
    try {
      await api.post('/api/whatsapp/send', { number: phone, message: previewMsg });
      await api.put(`/ordens-servico/${os._id}`, { status: 'Pronto na loja', registroTexto: '🤖 Mensagem de Pronto na Loja enviada.' });
      
      showToast('Aviso de Retirada enviado com sucesso!', 'success');
      onUpdate(); 
      fetchData();
    } catch (error) { 
      showToast('Erro ao enviar mensagem.', 'error'); 
    } finally {
      setSaving(false);
    }
  };

  if(!os) return null;

  const isBoasVindasScreen = os.statusmsg === 'Pendente' || status === 'Pedido da lente' || status === 'Pedido realizado';

  return (
    <div className="modal-overlay">
      <div className="modal-content animate-slide-up">
        <div className="modal-header">
            <div className="modal-title-group">
               <div className="flex-center">
                   <h2 className="modal-cv">#{os.produto?.numeroCV || '---'}</h2>
                   <Badge status={status} />
               </div>
               <p className="modal-client">{safeRenderName(os.cliente)}</p>
            </div>
            <button onClick={onClose} className="btn-close-modal"><Icons.Close /></button>
        </div>

        <div className="tabs-container">
           {['geral', 'receita', 'automacao', 'galeria', 'editar'].map(tab => (
               <button key={tab} className={`tab-btn ${activeTab === tab ? 'active' : ''}`} onClick={() => setActiveTab(tab)}>
                   {tab === 'geral' && <Icons.User />} {tab === 'receita' && <Icons.Eye />} {tab === 'automacao' && <Icons.Whatsapp />} {tab === 'galeria' && <Icons.Image />} {tab === 'editar' && <Icons.Edit />}
                   <span style={{textTransform:'capitalize'}}>{tab}</span>
               </button>
           ))}
        </div>

        <div className="modal-body">
           {activeTab === 'geral' && (
             <div className="grid-2-col">
                <div className="panel">
                   <div className="panel-header-row">
                       <h4 className="panel-title">Detalhes do Pedido</h4>
                       <span className="total-value">{formatCurrency(os.valorTotal)}</span>
                   </div>
                   <div className="info-stack">
                      <div className="flex-row">
                         <InfoRow label="Loja" value={os.loja} />
                         <InfoRow label="Vendedor" value={os.vendedor} />
                      </div>
                      <div className="flex-row">
                        {/* USO DE formatDateLocal PARA DATA CORRETA */}
                        <InfoRow label="Data da Compra" value={formatDateLocal(os.dataCompra)} />
                        <InfoRow label="Previsão Entrega" value={formatDateLocal(os.dataEntrega)} />
                      </div>
                      <InfoRow label="Paciente" value={safeRenderName(os.paciente)} />
                      <InfoRow label="WhatsApp" value={os.whatsapp} />
                      <div className="divider" />
                      <InfoRow label="Lente" value={os.produto?.lente} />
                      <InfoRow label="Armação" value={os.produto?.armacao} />
                      <div className="divider" />
                      <InfoRow label="Endereço" value={os.endereco ? `${os.endereco.logradouro}, ${os.endereco.numero} - ${os.endereco.bairro}` : 'Não informado'} />
                      <InfoRow label="CPF na Nota" value={os.cliente?.cpf} />
                   </div>
                </div>
                
                <div className="panel">
                   <h4 className="panel-title">Atualizar Status</h4>
                   <label className="input-label">Novo Status</label>
                   <select className="form-select" value={status} onChange={e=>setStatus(e.target.value)}>
                      <option value="Pedido da lente">Pedido da lente</option>
                      <option value="Pedido realizado">Pedido realizado</option>
                      <option value="No laboratório">No laboratório</option>
                      <option value="Pronto na loja">Pronto na loja</option>
                      <option value="Entregue">Entregue</option>
                   </select>
                   <label className="input-label" style={{marginTop:15}}>Observação Interna</label>
                   <input className="form-input" value={obs} onChange={e=>setObs(e.target.value)} placeholder="Ex: Chegou do laboratório..." />
                   <button onClick={handleSaveStatus} disabled={saving} className="btn-primary" style={{marginTop: 20}}>
                      {saving ? 'Salvando...' : 'Salvar Status'}
                   </button>
                </div>

                <div className="panel full-width">
                   <h4 className="panel-title">Histórico</h4>
                   <div className="history-box">
                      {os.registros?.slice(0).reverse().map((r, i) => (
                        <div key={i} className="history-item">
                           <span className="history-date">{new Date(r.createdAt).toLocaleString()}</span>
                           <span className="history-text">{r.texto}</span>
                        </div>
                      ))}
                   </div>
                </div>
             </div>
           )}

           {activeTab === 'receita' && (
             <div className="panel">
                <h4 className="panel-title">Dados da Receita</h4>
                <div className="rx-table-wrapper">
                  <table className="rx-table">
                    <thead><tr><th>Olho</th><th>Esf</th><th>Cil</th><th>Eixo</th><th>DNP</th></tr></thead>
                    <tbody>
                       <tr><td>OD</td><td>{os.receita?.od?.esf}</td><td>{os.receita?.od?.cil}</td><td>{os.receita?.od?.eixo}</td><td>{os.receita?.od?.dnp}</td></tr>
                       <tr><td>OE</td><td>{os.receita?.oe?.esf}</td><td>{os.receita?.oe?.cil}</td><td>{os.receita?.oe?.eixo}</td><td>{os.receita?.oe?.dnp}</td></tr>
                    </tbody>
                  </table>
                </div>
                <div style={{marginTop:15, fontSize: 14}}><strong>Adição:</strong> {os.receita?.adicao || '-'}</div>
             </div>
           )}

           {activeTab === 'automacao' && (
             <div className="grid-2-col">
                <div className="panel">
                   <h4 className="panel-title">Disparos de Mensagem</h4>
                   
                   {isBoasVindasScreen ? (
                     <>
                        <div className="chat-bubble" style={{background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0', fontStyle: 'italic'}}>
                           🤖 <strong>Importante:</strong> Esta é a mensagem inicial (Boas-Vindas). O servidor irá gerar o texto automaticamente e enviar o flyer.
                        </div>
                        <button onClick={handleDispararBoasVindas} disabled={saving} className="btn-whatsapp">
                           <Icons.Send /> 
                           {saving ? 'Conectando...' : (os.statusmsg === 'Enviada' ? 'Reenviar Boas-Vindas' : 'Disparar Boas-Vindas (Com Foto)')}
                        </button>
                     </>
                   ) : status === 'Pronto na loja' ? (
                     <>
                        <div className="chat-bubble">{previewMsg}</div>
                        <button onClick={handleSendWhatsapp} disabled={saving} className="btn-whatsapp">
                           <Icons.Send /> {saving ? 'Enviando...' : 'Avisar Retirada ao Cliente'}
                        </button>
                     </>
                   ) : (
                     <p className="text-muted">Altere o status para "Pronto na loja" para ver o aviso de retirada.</p>
                   )}
                </div>
             </div>
           )}

           {activeTab === 'galeria' && (
             <div className="gallery-grid">
                {os.anexos?.map((url, i) => (
                   <div key={i} className="image-card" onClick={()=>setFotoExpandida(url)}>
                      <img src={url} alt="Anexo" />
                   </div>
                ))}
                {!os.anexos?.length && <p className="text-muted">Sem fotos anexadas.</p>}
             </div>
           )}

           {/* ABA DE EDIÇÃO COMPLETA */}
           {activeTab === 'editar' && (
             <div className="panel">
                <h4 className="panel-title">Editar Informações (Completo)</h4>
                
                <div className="grid-2-col">
                    {/* COLUNA 1: IDENTIFICAÇÃO E CLIENTE */}
                    <div>
                        <h5 className="sub-title">Identificação</h5>
                        <div className="flex-row gap-3">
                            <div style={{flex:1}}>
                                <label className="input-label">Loja</label>
                                <select className="form-select" value={editForm.loja} onChange={e => setEditForm({...editForm, loja: e.target.value})}>
                                    <option>Vizum 01</option>
                                    <option>Vizum 02</option>
                                </select>
                            </div>
                            <div style={{flex:1}}>
                                <label className="input-label">Vendedor</label>
                                <input className="form-input" value={editForm.vendedor} onChange={e => setEditForm({...editForm, vendedor: e.target.value})} />
                            </div>
                        </div>

                        <h5 className="sub-title mt-3">Cliente</h5>
                        <label className="input-label">Nome Completo</label>
                        <input className="form-input" value={editForm.cliente} onChange={e => setEditForm({...editForm, cliente: e.target.value})} />
                        
                        <div className="flex-row gap-3 mt-3">
                             <div style={{flex:1}}>
                                <label className="input-label">CPF</label>
                                <input className="form-input" value={editForm.cpf} onChange={e => setEditForm({...editForm, cpf: e.target.value})} />
                             </div>
                             <div style={{flex:1}}>
                                <label className="input-label">Nascimento</label>
                                <input type="date" className="form-input" value={editForm.dataNascimento} onChange={e => setEditForm({...editForm, dataNascimento: e.target.value})} />
                             </div>
                        </div>

                        <label className="input-label mt-3">Paciente (Usuário)</label>
                        <input className="form-input" value={editForm.paciente} onChange={e => setEditForm({...editForm, paciente: e.target.value})} />

                        <label className="input-label mt-3">WhatsApp</label>
                        <input className="form-input" value={editForm.whatsapp} onChange={e => setEditForm({...editForm, whatsapp: e.target.value})} />
                    </div>

                    {/* COLUNA 2: ENDEREÇO E PRODUTO */}
                    <div>
                        <h5 className="sub-title">Endereço</h5>
                        <div className="flex-row gap-3">
                            <div style={{width:'100px'}}>
                                <label className="input-label">CEP</label>
                                <input className="form-input" value={editForm.cep} onChange={e => setEditForm({...editForm, cep: e.target.value})} />
                            </div>
                            <div style={{flex:1}}>
                                <label className="input-label">Logradouro</label>
                                <input className="form-input" value={editForm.endereco} onChange={e => setEditForm({...editForm, endereco: e.target.value})} />
                            </div>
                        </div>
                        <div className="flex-row gap-3 mt-3">
                            <div style={{flex:1}}>
                                <label className="input-label">Número</label>
                                <input className="form-input" value={editForm.numero} onChange={e => setEditForm({...editForm, numero: e.target.value})} />
                            </div>
                            <div style={{flex:1}}>
                                <label className="input-label">Bairro</label>
                                <input className="form-input" value={editForm.bairro} onChange={e => setEditForm({...editForm, bairro: e.target.value})} />
                            </div>
                        </div>

                        <h5 className="sub-title mt-3">Produto & Datas</h5>
                        <div className="flex-row gap-3">
                            <div style={{flex:2}}>
                                <label className="input-label">Lente</label>
                                <input className="form-input" value={editForm.lente} onChange={e => setEditForm({...editForm, lente: e.target.value})} />
                            </div>
                            <div style={{flex:1}}>
                                <label className="input-label">Cód CV</label>
                                <input className="form-input" value={editForm.cv} onChange={e => setEditForm({...editForm, cv: e.target.value})} />
                            </div>
                        </div>
                        
                        <label className="input-label mt-3">Armação</label>
                        <input className="form-input" value={editForm.armacao} onChange={e => setEditForm({...editForm, armacao: e.target.value})} />

                         <div className="flex-row mt-3 gap-3">
                            <div style={{flex:1}}>
                                <label className="input-label">Data Compra</label>
                                <input type="date" className="form-input" value={editForm.dataCompra} onChange={e => setEditForm({...editForm, dataCompra: e.target.value})} />
                            </div>
                            <div style={{flex:1}}>
                                <label className="input-label">Previsão Entrega</label>
                                <input type="date" className="form-input" value={editForm.dataEntrega} onChange={e => setEditForm({...editForm, dataEntrega: e.target.value})} />
                            </div>
                        </div>
                        
                        <div className="flex-row mt-3 gap-3">
                            <div style={{flex:1}}>
                                <label className="input-label">URL Nota Fiscal (PDF)</label>
                                <input className="form-input" value={editForm.nfceUrl} onChange={e => setEditForm({...editForm, nfceUrl: e.target.value})} placeholder="Link do PDF" />
                            </div>
                            <div style={{flex:1}}>
                                <label className="input-label">Chave NF-e</label>
                                <input className="form-input" value={editForm.chaveNfe} onChange={e => setEditForm({...editForm, chaveNfe: e.target.value})} />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="finance-section">
                     <h4 className="panel-title">Financeiro</h4>
                     <div className="finance-grid">
                        <div style={{flex: 1, minWidth: '200px'}}>
                            <label className="input-label">Total (R$)</label>
                            <input className="form-input highlight" value={editForm.valorTotal} onChange={e => setEditForm({...editForm, valorTotal: e.target.value})} placeholder="0,00" />
                        </div>
                        <div className="add-payment-group">
                             <div style={{flex: 1}}>
                                <label className="input-label">Método</label>
                                <select className="form-select" value={novoPagamento.metodo} onChange={e => setNovoPagamento({...novoPagamento, metodo: e.target.value})}>
                                    {metodosPagamento.map(m => <option key={m}>{m}</option>)}
                                </select>
                             </div>
                             {novoPagamento.metodo === 'Crédito' && (
                               <div style={{width: '60px'}}>
                                  <label className="input-label">Px</label>
                                  <input type="number" min="1" max="12" className="form-input" value={novoPagamento.parcelas} onChange={e => setNovoPagamento({...novoPagamento, parcelas: e.target.value})} />
                               </div>
                             )}
                             <div style={{flex: 1}}>
                                <label className="input-label">Valor</label>
                                <input type="text" className="form-input" placeholder="0,00" value={novoPagamento.valor} onChange={e => setNovoPagamento({...novoPagamento, valor: e.target.value})} />
                             </div>
                             <button onClick={addPayment} className="btn-icon-primary"><Icons.Plus /></button>
                        </div>
                     </div>

                     <div className="finance-summary-grid">
                       <div style={{flex: 1}}>
                           <label className="input-label">Histórico de Pagamentos</label>
                           {editForm.pagamentos.length === 0 ? <p className="text-muted small">Nenhum pagamento registrado.</p> : (
                               <div className="payment-list">
                                  {editForm.pagamentos.map((p, idx) => (
                                      <div key={idx} className="payment-item">
                                          <div className="payment-info">
                                              <span className="payment-badge">{p.metodo} {p.parcelas > 1 ? `(${p.parcelas}x)` : ''}</span>
                                              <span className="payment-value">{formatCurrency(p.valor)}</span>
                                          </div>
                                          <button onClick={() => removePayment(p.id || p._id)} className="btn-trash"><Icons.Trash /></button>
                                      </div>
                                  ))}
                               </div>
                           )}
                       </div>
                       <div className="summary-box">
                          <div className="summary-row"><span>Total</span><strong>{formatCurrency(valorTotalNum)}</strong></div>
                          <div className="summary-row"><span>Pago</span><strong className="text-success">{formatCurrency(totalPago)}</strong></div>
                          <div className="divider"></div>
                          <div className="summary-row"><span>Restante</span><strong className={restante > 0.01 ? 'text-danger' : 'text-success'}>{restante <= 0.01 ? 'QUITADO' : formatCurrency(restante)}</strong></div>
                       </div>
                    </div>
                </div>
                <div className="modal-footer">
                    <button onClick={handleEditSave} disabled={saving} className="btn-primary">{saving ? 'Salvando...' : 'Salvar Alterações'}</button>
                </div>
             </div>
           )}
        </div>
      </div>
      
      {fotoExpandida && (
        <div className="zoom-overlay" onClick={()=>setFotoExpandida(null)}>
           <img src={fotoExpandida} className="zoom-img" alt="Zoom" />
        </div>
      )}
      <Toast msg={toast?.msg} type={toast?.type} />
    </div>
  );
}

// =============================================================
// GLOBAL STYLES (CSS-IN-JS REFINADO)
// =============================================================
const GlobalStyles = () => (
<style>{`
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
    
    :root {
      --primary: ${THEME.primary};
      --text: ${THEME.text};
      --bg: ${THEME.bg};
      --border: ${THEME.border};
      --surface: ${THEME.surface};
      --muted: ${THEME.muted};
    }

    /* Layout Geral */
    .dashboard-container { padding: 40px; max-width: 1600px; margin: 0 auto; font-family: 'Plus Jakarta Sans', sans-serif; background: var(--bg); min-height: 100vh; box-sizing: border-box; }
    
    /* Header */
    .dash-header { display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 40px; flex-wrap: wrap; gap: 20px; }
    .header-left { display: flex; flex-direction: column; gap: 8px; }
    .btn-back { background: none; border: none; font-size: 14px; cursor: pointer; color: var(--muted); font-weight: 600; padding: 0; display: flex; align-items: center; gap: 5px; transition: 0.2s; }
    .btn-back:hover { color: var(--primary); transform: translateX(-3px); }
    .page-title { margin: 0; fontSize: 32px; color: var(--text); fontWeight: 800; letter-spacing: -1px; }
    .page-subtitle { margin: 0; color: var(--muted); fontSize: 16px; }
    .total-badge { background: #fff; color: var(--text); padding: 12px 24px; borderRadius: 16px; display: flex; flex-direction: column; align-items: flex-end; box-shadow: 0 4px 20px -5px rgba(0,0,0,0.08); border: 1px solid var(--border); }
    .badge-label { font-size: 11px; text-transform: uppercase; color: var(--muted); font-weight: 700; }
    .badge-value { font-size: 28px; font-weight: 800; line-height: 1; color: var(--primary); }

    /* Cards KPI */
    .stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 20px; margin-bottom: 30px; }
    .stat-card-premium { background: #fff; border-radius: 20px; padding: 24px; cursor: pointer; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); border: 1px solid transparent; position: relative; overflow: hidden; display: flex; flex-direction: column; gap: 15px; }
    .stat-card-premium:hover { transform: translateY(-3px); }
    .stat-card-premium.active { background: #fff; border-color: var(--primary); }
    .stat-card-header { display: flex; justify-content: space-between; align-items: flex-start; }
    .stat-icon-bg { width: 44px; height: 44px; border-radius: 12px; display: flex; align-items: center; justify-content: center; transition: 0.3s; }
    .stat-count { font-size: 32px; font-weight: 800; }
    .stat-label { font-size: 13px; font-weight: 700; color: var(--muted); text-transform: uppercase; letter-spacing: 0.5px; }
    .active-indicator { position: absolute; bottom: 0; left: 0; width: 100%; height: 4px; }

    /* Toolbar */
    .toolbar-container { background: #fff; padding: 16px; border-radius: 20px; display: flex; flex-wrap: wrap; gap: 16px; align-items: center; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.02); border: 1px solid var(--border); margin-bottom: 30px; }
    .search-wrapper { display: flex; alignItems: center; gap: 12px; background: var(--bg); padding: 10px 16px; border-radius: 12px; border: 1px solid transparent; flex: 2; min-width: 280px; transition: 0.2s; }
    .search-wrapper:focus-within { border-color: var(--primary); background: #fff; box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1); }
    .search-input { border: none; outline: none; background: transparent; width: 100%; font-size: 15px; color: var(--text); font-weight: 500; font-family: inherit; }
    
    .filters-wrapper { display: flex; gap: 12px; flex-wrap: wrap; flex: 3; justify-content: flex-end; align-items: center; }
    .date-group { display: flex; align-items: center; gap: 10px; background: var(--bg); padding: 8px 16px; border-radius: 12px; border: 1px solid transparent; }
    .input-label { font-size: 11px; font-weight: 700; color: var(--muted); text-transform: uppercase; }
    .date-input { border: none; background: transparent; font-size: 13px; font-weight: 600; color: var(--text); outline: none; font-family: inherit; }
    .date-separator { color: var(--muted); font-size: 12px; }
    .select-input { padding: 10px 16px; border-radius: 12px; border: 1px solid var(--border); background: var(--bg); font-size: 14px; outline: none; font-weight: 500; cursor: pointer; color: var(--text); }
    .btn-month, .btn-clear { display: flex; align-items: center; justify-content: center; width: 40px; height: 40px; border-radius: 10px; cursor: pointer; border: none; transition: 0.2s; }
    .btn-month { background: #e0e7ff; color: var(--primary); }
    .btn-month:hover { background: var(--primary); color: #fff; }
    .btn-clear { background: #fee2e2; color: #ef4444; }
    .btn-clear:hover { background: #ef4444; color: #fff; }

    /* Lista e Cards de OS */
    .grid-cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 24px; }
    .empty-state { grid-column: 1/-1; text-align: center; padding: 60px; background: #fff; border-radius: 24px; border: 2px dashed var(--border); display: flex; flex-direction: column; align-items: center; gap: 15px; color: var(--muted); }
    .empty-icon { width: 60px; height: 60px; background: #f1f5f9; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: var(--muted); }
    .btn-link { background: none; border: none; color: var(--primary); text-decoration: underline; cursor: pointer; font-weight: 600; }
    
    .os-card { background: #fff; border-radius: 20px; border: 1px solid var(--border); cursor: pointer; transition: all 0.3s ease; overflow: hidden; display: flex; flex-direction: column; position: relative; }
    .os-card:hover { transform: translateY(-5px); box-shadow: 0 15px 30px -10px rgba(0,0,0,0.1); border-color: var(--primary); }
    .os-card-header { padding: 16px 24px; border-bottom: 1px solid var(--bg); display: flex; justify-content: space-between; align-items: center; }
    .card-badges { display: flex; gap: 8px; }
    .cv-badge { font-weight: 800; font-size: 11px; color: var(--primary); background: #eef2ff; padding: 4px 8px; border-radius: 6px; }
    .os-number-badge { font-weight: 800; font-size: 11px; color: #64748b; background: #f1f5f9; padding: 4px 8px; border-radius: 6px; }
    .store-text { font-size: 11px; font-weight: 700; text-transform: uppercase; color: var(--muted); letter-spacing: 0.5px; }
    .os-card-body { padding: 24px; flex: 1; }
    .client-name { margin: 0 0 8px 0; font-size: 18px; color: var(--text); font-weight: 700; line-height: 1.2; }
    .lens-info { display: inline-flex; align-items: center; gap: 8px; font-size: 13px; background: #f8fafc; padding: 6px 12px; border-radius: 8px; color: var(--text); font-weight: 600; border: 1px solid var(--bg); }
    .no-lens { margin: 0; font-size: 13px; color: var(--muted); font-style: italic; }
    .progress-bar-wrapper { margin-top: 15px; }
    .progress-labels { display: flex; justify-content: space-between; font-size: 11px; color: var(--muted); font-weight: 600; margin-bottom: 4px; }
    .progress-track { width: 100%; height: 6px; background: #f1f5f9; border-radius: 4px; overflow: hidden; }
    .progress-fill { height: 100%; border-radius: 4px; transition: width 0.5s ease; }
    
    .alert-stack { padding: 0 24px; display: flex; flex-direction: column; gap: 6px; }
    .alert-box { padding: 8px 12px; font-size: 11px; font-weight: 700; display: flex; align-items: center; gap: 8px; border-radius: 8px; }
    .alert-orange { background: #fff7ed; color: #c2410c; }
    .alert-green { background: #f0fdf4; color: #15803d; }
    .os-card-footer { padding: 16px 24px; border-top: 1px solid var(--bg); display: flex; justify-content: space-between; align-items: center; background: #fafafa; margin-top: 15px; }
    .date-info { display: flex; flex-direction: column; }
    .date-label { font-size: 10px; color: var(--muted); font-weight: 700; }
    .date-value { font-size: 13px; font-weight: 600; color: var(--text); }

    /* Modal */
    .modal-overlay { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.6); z-index: 999; display: flex; justify-content: center; alignItems: center; padding: 20px; backdrop-filter: blur(5px); }
    .modal-content { background: #fff; width: 100%; max-width: 900px; max-height: 90vh; border-radius: 24px; display: flex; flex-direction: column; box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25); overflow: hidden; }
    .modal-header { padding: 24px 32px; border-bottom: 1px solid var(--border); display: flex; justify-content: space-between; align-items: flex-start; background: #fff; }
    .modal-title-group { display: flex; flex-direction: column; gap: 4px; }
    .flex-center { display: flex; align-items: center; gap: 12px; }
    .modal-cv { margin: 0; fontSize: 24px; fontWeight: 800; color: var(--text); }
    .modal-client { margin: 0; color: var(--muted); fontSize: 15px; font-weight: 500; }
    .btn-close-modal { background: #f1f5f9; border: none; border-radius: 50%; width: 36px; height: 36px; cursor: pointer; display: flex; align-items: center; justify-content: center; color: var(--text); transition: 0.2s; }
    .btn-close-modal:hover { background: #e2e8f0; }

    .tabs-container { display: flex; padding: 0 32px; border-bottom: 1px solid var(--border); gap: 20px; background: #fff; overflow-x: auto; }
    .tab-btn { padding: 16px 0; background: none; border: none; border-bottom: 3px solid transparent; color: var(--muted); font-weight: 600; cursor: pointer; display: flex; gap: 8px; align-items: center; transition: 0.2s; font-size: 14px; white-space: nowrap; }
    .tab-btn.active { border-color: var(--primary); color: var(--primary); }
    .tab-btn:hover:not(.active) { color: var(--text); }

    .modal-body { padding: 32px; overflow-y: auto; flex: 1; background: #f9fafb; }
    .grid-2-col { display: grid; grid-template-columns: repeat(auto-fit, minmax(350px, 1fr)); gap: 24px; }
    .panel { background: #fff; padding: 24px; border-radius: 20px; border: 1px solid var(--border); box-shadow: 0 1px 3px rgba(0,0,0,0.02); }
    .panel.full-width { grid-column: 1/-1; }
    .panel-header-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; border-bottom: 1px solid var(--bg); padding-bottom: 10px; }
    .panel-title { margin: 0; fontSize: 14px; font-weight: 700; text-transform: uppercase; color: var(--muted); letter-spacing: 0.5px; }
    .sub-title { fontSize: 13px; font-weight: 700; color: var(--primary); margin: 0 0 10px 0; padding-bottom: 5px; border-bottom: 1px dashed var(--border); }
    .total-value { font-size: 20px; font-weight: 800; color: var(--primary); }
    
    .info-stack { display: flex; flex-direction: column; gap: 8px; }
    .flex-row { display: flex; gap: 20px; }
    .mt-3 { margin-top: 15px; }
    .gap-3 { gap: 15px; }

    .form-input, .form-select { width: 100%; padding: 10px 12px; border-radius: 10px; border: 1px solid var(--border); background: #f8fafc; font-size: 14px; outline: none; transition: 0.2s; font-family: inherit; color: var(--text); }
    .form-input:focus, .form-select:focus { border-color: var(--primary); background: #fff; box-shadow: 0 0 0 3px rgba(79,70,229,0.1); }
    .form-input.highlight { background: #eff6ff; color: var(--primary); font-weight: 700; font-size: 16px; }

    .btn-primary { width: 100%; background: var(--text); color: #fff; border: none; padding: 14px; border-radius: 12px; font-weight: 700; cursor: pointer; font-size: 14px; transition: 0.2s; }
    .btn-primary:hover { background: #000; transform: translateY(-1px); }
    
    .history-box { max-height: 250px; overflow-y: auto; background: #f8fafc; border-radius: 12px; padding: 10px; }
    .history-item { font-size: 13px; margin-bottom: 8px; padding: 8px; border-bottom: 1px solid var(--border); display: flex; gap: 12px; }
    .history-date { font-weight: 700; color: var(--primary); min-width: 130px; font-size: 12px; }
    
    .rx-table-wrapper { overflow-x: auto; border: 1px solid var(--border); border-radius: 12px; }
    .rx-table { width: 100%; border-collapse: collapse; font-size: 14px; background: #fff; text-align: center; }
    .rx-table th { background: #f1f5f9; padding: 10px; font-size: 12px; color: var(--muted); text-transform: uppercase; }
    .rx-table td { padding: 10px; border-top: 1px solid var(--border); }

    .chat-bubble { background: #dcfce7; padding: 16px; border-radius: 16px; border: 1px solid #bbf7d0; font-size: 14px; color: #14532d; white-space: pre-line; line-height: 1.5; margin-bottom: 15px; }
    .btn-whatsapp { width: 100%; background: #25D366; color: white; border: none; padding: 14px; border-radius: 12px; font-weight: 700; cursor: pointer; display: flex; alignItems: center; justifyContent: center; gap: 10px; }
    
    .gallery-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(120px, 1fr)); gap: 16px; }
    .image-card { aspect-ratio: 1/1; border-radius: 12px; overflow: hidden; cursor: zoom-in; border: 1px solid var(--border); transition: 0.2s; }
    .image-card:hover { transform: scale(1.02); }
    .image-card img { width: 100%; height: 100%; object-fit: cover; }
    .zoom-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.9); z-index: 2000; display: flex; align-items: center; justify-content: center; backdrop-filter: blur(5px); }
    .zoom-img { max-width: 90vw; max-height: 90vh; border-radius: 12px; box-shadow: 0 20px 50px rgba(0,0,0,0.5); }

    /* Financeiro no Modal */
    .finance-section { margin-top: 24px; border-top: 1px dashed var(--border); padding-top: 24px; }
    .finance-grid { display: flex; flex-wrap: wrap; gap: 16px; margin-bottom: 24px; }
    .add-payment-group { flex: 2; min-width: 280px; display: flex; gap: 8px; align-items: flex-end; }
    .btn-icon-primary { width: 42px; height: 42px; border-radius: 10px; border: none; background: var(--primary); color: #fff; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: 0.2s; }
    .btn-icon-primary:hover { background: #4338ca; }
    
    .finance-summary-grid { display: flex; gap: 30px; flex-wrap: wrap; }
    .payment-list { display: flex; flex-direction: column; gap: 8px; margin-top: 10px; }
    .payment-item { display: flex; justify-content: space-between; align-items: center; padding: 10px 14px; background: #f8fafc; border-radius: 10px; font-size: 13px; border: 1px solid var(--border); }
    .payment-badge { font-weight: 700; color: var(--text); background: #e2e8f0; padding: 2px 6px; border-radius: 4px; font-size: 11px; text-transform: uppercase; margin-right: 8px; }
    .btn-trash { background: none; border: none; color: #ef4444; cursor: pointer; opacity: 0.7; transition: 0.2s; padding: 4px; }
    .btn-trash:hover { opacity: 1; background: #fee2e2; border-radius: 4px; }
    
    .summary-box { minWidth: 240px; background: #f8fafc; border: 1px solid var(--border); border-radius: 16px; padding: 20px; display: flex; flex-direction: column; gap: 10px; }
    .summary-row { display: flex; justify-content: space-between; font-size: 14px; color: var(--text); }
    .divider { height: 1px; background: var(--border); margin: 5px 0; }
    .text-success { color: #15803d; }
    .text-danger { color: #ef4444; }

    @keyframes spin { to { transform: rotate(360deg); } }
    @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
    .animate-slide-up { animation: slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1); }
    .spinner { width: 30px; height: 30px; border: 3px solid var(--border); border-top-color: var(--primary); border-radius: 50%; animation: spin 0.8s linear infinite; margin: 0 auto 15px; }
    .loading-state { text-align: center; padding: 80px; color: var(--muted); }
  `}</style>
);